<template>
  <div class="dynamic-field">
    <label :for="field.key">
      {{ field.title }}
      <span v-if="!field.optional" class="dynamic-field__required">*</span>
    </label>
    
    <!-- Input field -->
    <input
      v-if="field.type === 'input'"
      :id="field.key"
      type="text"
      v-model="fieldValue"
      :placeholder="`Введите ${field.title.toLowerCase()}`"
      :class="{ error: error }"
    />

    <!-- Number input field -->
    <input
      v-else-if="field.type === 'input_number'"
      :id="field.key"
      type="number"
      v-model.number="fieldValue"
      :placeholder="`Введите ${field.title.toLowerCase()}`"
      :class="{ error: error }"
    />
    
    <!-- Select field -->
    <select
      v-else-if="field.type === 'select'"
      :id="field.key"
      v-model="fieldValue"
      :class="{ error: error }"
    >
      <option value="" disabled selected>{{ selectPlaceholder }}</option>
      <option
        v-for="value in field.values"
        :key="value"
        :value="value"
      >
        {{ value }}
      </option>
    </select>
    
    <!-- Multiselect field -->
    <div v-else-if="field.type === 'multiselect'" class="dynamic-field__multiselect">
      <div class="dynamic-field__multiselectContainer">
        <button
          type="button"
          class="dynamic-field__multiselectBtn"
          @click="toggleMultiselect"
          :class="{ error: error, open: isMultiselectOpen }"
        >
          <span>{{ multiselectDisplay }}</span>
          <img src="../../assets/images/down-arrow.svg" alt="open" />
        </button>
        <div 
          v-if="isMultiselectOpen" 
          class="dynamic-field__multiselectDropdown"
        >
          <div
            v-for="value in field.values"
            :key="value"
            class="dynamic-field__multiselectItem"
            @click="toggleMultiselectValue(value)"
          >
            <input
              type="checkbox"
              :checked="isValueSelected(value)"
              @change="toggleMultiselectValue(value)"
            />
            <span>{{ value }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Checkbox field (radio behavior) -->
    <div v-else-if="field.type === 'checkbox'" class="dynamic-field__checkbox">
      <div
        v-for="value in field.values"
        :key="value"
        class="dynamic-field__checkboxItem"
      >
        <input
          :id="`${field.key}_${value}`"
          type="radio"
          :name="field.key"
          :value="value"
          v-model="fieldValue"
        />
        <label :for="`${field.key}_${value}`">{{ value }}</label>
      </div>
    </div>
    
    <span v-if="error" class="dynamic-field__error">
      {{ error }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'DynamicField',
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      default: null
    },
    error: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isMultiselectOpen: false,
      inputTimeout: null
    }
  },
  computed: {
    fieldValue: {
      get() {
        return this.value
      },
      set(newValue) {
        if (this.field.type === 'input' || this.field.type === 'input_number') {
          if (this.inputTimeout) {
            clearTimeout(this.inputTimeout)
          }
          this.inputTimeout = setTimeout(() => {
            this.$emit('update', newValue)
          }, 300)
        } else {
          this.$emit('update', newValue)
        }
      }
    },
    selectPlaceholder() {
      return `Выберите ${this.field.title.toLowerCase()}`
    },
    multiselectDisplay() {
      if (!this.fieldValue || this.fieldValue.length === 0) {
        return 'Выберите значения'
      }
      if (this.fieldValue.length === this.field.values.length) {
        return 'Все значения'
      }
      if (this.fieldValue.length === 1) {
        return this.fieldValue[0]
      }
      return `Выбрано ${this.fieldValue.length} значений`
    }
  },
  methods: {
    toggleMultiselect() {
      this.isMultiselectOpen = !this.isMultiselectOpen
    },
    toggleMultiselectValue(value) {
      if (!this.fieldValue) {
        this.fieldValue = []
      }
      
      const currentValues = [...this.fieldValue]
      const index = currentValues.indexOf(value)
      
      if (index > -1) {
        currentValues.splice(index, 1)
      } else {
        currentValues.push(value)
      }
      
      this.fieldValue = currentValues
    },
    isValueSelected(value) {
      return this.fieldValue && this.fieldValue.includes(value)
    }
  },
  beforeDestroy() {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout)
    }
  }
}
</script>

<style lang="scss" scoped>
.dynamic-field {
  display: flex;
  flex-direction: column;
  gap: 12px;

  label {
    color: white;
    font-size: 16px;
    font-weight: 600;
  }

  &__required {
    color: #ff4757;
    margin-left: 4px;
  }

  input[type="text"],
  input[type="number"] {
    background: #1a1f3e;
    border: 1px solid #646464;
    border-radius: 6px;
    padding: 12px 16px;
    color: white;
    font-size: 14px;
    font-family: Gilroy, sans-serif;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #7457ff;
    }

    &::placeholder {
      color: #888;
    }

    &.error {
      border-color: #ff4757;
    }
  }

  select {
    background: #1a1f3e;
    border: 1px solid #646464;
    border-radius: 6px;
    padding: 12px 16px;
    color: white;
    font-size: 14px;
    font-family: Gilroy, sans-serif;
    transition: all 0.3s ease;
    cursor: pointer;
    appearance: none;
    background-image: url('../../assets/images/down-arrow.svg');
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    padding-right: 40px;

    &:focus {
      outline: none;
      border-color: #7457ff;
    }

    &.error {
      border-color: #ff4757;
    }

    option {
      background: #1a1f3e;
      color: white;
    }

    option:disabled {
      color: #888;
    }

    &:invalid {
      color: #888;
    }
  }

  &__multiselect {
    position: relative;
  }

  &__multiselectContainer {
    position: relative;
  }

  &__multiselectBtn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #1a1f3e;
    border: 1px solid #646464;
    border-radius: 6px;
    padding: 12px 16px;
    color: white;
    font-size: 14px;
    font-family: Gilroy, sans-serif;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;
    text-align: left;

    &:hover {
      border-color: #7457ff;
    }

    &.error {
      border-color: #ff4757;
    }

    &.open {
      border-color: #7457ff;
    }

    img {
      width: 16px;
      height: 16px;
      filter: brightness(0) invert(0.6);
      transition: 0.3s;
    }

    &.open img {
      transform: rotate(180deg);
    }
  }

  &__multiselectDropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #1a1f3e;
    border: 1px solid #646464;
    border-radius: 6px;
    margin-top: 4px;
    z-index: 10;
    max-height: 200px;
    overflow-y: auto;
  }

  &__multiselectItem {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #2a2f4e;
    }

    input[type="checkbox"] {
      margin-right: 12px;
      width: 16px;
      height: 16px;
      accent-color: #7457ff;
    }

    span {
      color: white;
      font-size: 14px;
    }
  }

  &__checkbox {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__checkboxItem {
    display: flex;
    align-items: center;
    gap: 12px;

    input[type="radio"] {
      width: 16px;
      height: 16px;
      accent-color: #7457ff;
    }

    label {
      color: white;
      font-size: 14px;
      font-weight: 400;
      cursor: pointer;
      margin: 0;
    }
  }

  &__error {
    color: #ff4757;
    font-size: 13px;
    font-weight: 500;
  }
}
</style>
